import {
  Avatar,
  Body,
  Heading,
  Icon,
  IconType,
} from '@assembly-js/design-system';
import { createSelector } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { ReactNode } from 'react';

import { User } from 'src/constants';
import {
  WidgetContainer,
  WidgetContent,
  WidgetDraft,
  WidgetHeader,
  WidgetItem,
} from 'src/features/assistant/action-item/Widget';
import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import { CTA } from 'src/types/stream-chat';
import { DateUtils } from 'src/utils';
import { isTask, Task, TaskStatus } from 'src/utils/TaskAppUtils';

const TaskPreviewRow = ({
  label,
  content,
  contentImage,
}: {
  label: string;
  content: string;
  contentImage: ReactNode;
}) => (
  <>
    <Body className="text-gray-small" size="sm">
      {label}
    </Body>
    <div className="flex items-center justify-start gap-1.5">
      {contentImage}
      <Body size="sm">{content}</Body>
    </div>
  </>
);

const TaskStatusRow = ({ status }: { status: TaskStatus }) => {
  let content = 'To Do';
  let icon: IconType = 'ToDo';
  let iconClasses = 'text-light-gray';
  switch (status) {
    case 'inProgress':
      content = 'In Progress';
      icon = 'InProgress';
      iconClasses = 'text-warning-primary';
      break;
    case 'completed':
      content = 'Done';
      icon = 'SuccessSolid';
      iconClasses = 'text-success-primary';
      break;
  }
  return (
    <TaskPreviewRow
      label="Status"
      contentImage={
        <Icon className={clsx('w-4 h-4', iconClasses)} icon={icon} />
      }
      content={content}
    />
  );
};

type TaskAssigneeEntityType = 'client' | 'internal' | 'company';

const taskAssigneeSelector = createSelector(
  [
    (state: RootState) => state.clients.activeClients,
    (state: RootState) => state.users.internalUsers,
    (state: RootState) => state.clients.activeCompanies,
    (_, id: string) => id,
    (_, _id, entityType: TaskAssigneeEntityType) => entityType,
  ],
  (activeClients, internalUsers, activeCompanies, id, entityType) => {
    if (entityType === 'company') {
      const selectedCompany = activeCompanies.find(
        (company) => company.id === id,
      );
      return {
        name: selectedCompany?.fields.name ?? '',
        fallbackText: selectedCompany?.fields.name.at(0) ?? '',
        fallbackColor: selectedCompany?.fields.fallbackColor,
        avatarUrl: selectedCompany?.fields.avatarImageURL ?? '',
      };
    }

    let selectedUser: User | undefined;
    if (entityType === 'client') {
      selectedUser = activeClients.find((user) => user.id === id);
    } else {
      selectedUser = internalUsers.find((user) => user.id === id);
    }
    return {
      name: `${selectedUser?.fields.givenName ?? ''} ${selectedUser?.fields.familyName ?? ''}`,
      fallbackText: selectedUser?.fields.givenName?.at(0) ?? '',
      fallbackColor: selectedUser?.fields.fallbackColor,
      avatarUrl: selectedUser?.fields.avatarImageUrl ?? '',
    };
  },
);

const TaskAssigneeRow = ({
  internalUserId,
  clientId,
  companyId,
}: Pick<Task, 'internalUserId' | 'clientId' | 'companyId'>) => {
  let id = '';
  let entityType: TaskAssigneeEntityType = 'internal';
  if (internalUserId) {
    // assignee is an internal user
    id = internalUserId;
    entityType = 'internal';
  } else if (clientId) {
    // assignee is a client user
    id = clientId;
    entityType = 'client';
  } else {
    // assignee is a company
    id = companyId ?? '';
    entityType = 'company';
  }

  const { name, avatarUrl, fallbackText, fallbackColor } = useAppSelector(
    (state) => taskAssigneeSelector(state, id, entityType),
  );

  return (
    <TaskPreviewRow
      label="Assignee"
      contentImage={
        <Avatar
          className="w-4 h-4 border-none"
          alt={name}
          variant="circle"
          src={avatarUrl}
          size="sm"
          text={fallbackText}
          fallbackColor={fallbackColor}
        />
      }
      content={name}
    />
  );
};

export const TaskDraft = ({
  cta,
  handleCTAClick,
}: {
  cta: CTA;
  handleCTAClick: (cta: CTA) => void;
}) => {
  const task = cta.task_data;

  if (
    !isTask(task, false) ||
    (!task.internalUserId && !task.clientId && !task.companyId)
  ) {
    console.error('TaskDraft did not receive a valid task');
    return null;
  }

  return (
    <WidgetContainer>
      <WidgetHeader
        widgetName="TASK DRAFT"
        buttonName="Create task"
        buttonIcon="Plus"
        onClick={() => handleCTAClick(cta)}
      />
      <WidgetContent>
        <WidgetDraft>
          <Heading className="mb-2.5">{task.name}</Heading>
          <Body className="leading-5" size="sm">
            {task.description}
          </Body>
        </WidgetDraft>
        <WidgetItem className="border-l-2 border-r-0 border-t-0 border-b-0 border-solid border-non-hover-border pl-4 grid grid-cols-2 auto-rows-[28px] items-center mt-3">
          <TaskStatusRow status={task.status} />
          <TaskAssigneeRow
            internalUserId={task.internalUserId}
            clientId={task.clientId}
            companyId={task.companyId}
          />
          <TaskPreviewRow
            label="Due Date"
            contentImage={<Icon className="w-4 h-4" icon="Calendar" />}
            content={
              task.dueDate
                ? // removing the timezone from the string so it is parsed in the local timezone
                  DateUtils.formatDate(task.dueDate.split('T')[0], 'MMM D')
                : 'None'
            }
          />
        </WidgetItem>
      </WidgetContent>
    </WidgetContainer>
  );
};
